/**
 * FrameworkVersionsAdmin Component (HG-5A G5-01).
 *
 * The GCH-26 / GCH-27 registry surface: every framework version a tenant can
 * resolve against, with its publication record and its operator authority
 * approval record, plus the GCH-28 evidence export (GAT-13).
 *
 * The rows are not computed here. They come from one call to
 * public.framework_version_approval_records (migration 267), so the definition
 * of what is visible, what is read-only and what can be published lives in one
 * place and this surface cannot drift from it. That function is SECURITY
 * DEFINER for a stated reason: migration 232 gives the identifier ledger no
 * client RLS policy at all, so a security_invoker view would report every
 * version as never published. The visibility rule of framework_versions_read
 * is restated inside the function body.
 *
 * Publication (GCH-26) is migration 232's state machine, invoked and never
 * reimplemented. This screen calls publish_framework_version and shows what
 * comes back; a refusal is displayed verbatim, because the refusal text names
 * the CFX rule that produced it and paraphrasing it here would lose that.
 *
 * Two things this screen states rather than implies:
 *   - reference versions are read-only in every tenant (CFX-03 / GCH-20), so
 *     they are marked as such and offer no action;
 *   - "Not recorded" is not an empty value (GCH-35). An approval record that
 *     was never captured says so, and the export says so too.
 *
 * Permissions: Super Admin only, via the /admin/framework-versions route
 * wrapper. Publication is additionally gated inside the database function.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { BadgeCheck, Download, FileText, Loader, Lock, RefreshCw } from 'lucide-react';
import { useDocumentAPI } from '../../hooks/useDocumentAPI';

/** Only what the tenant filter needs; the full Airline type is not selected. */
interface AirlineOption {
  id: string;
  name: string;
}

/** One row of public.framework_version_approval_records (migration 267). */
interface ApprovalRecordRow {
  version_id: string;
  framework_id: string;
  version_identifier: string | null;
  version_label: string | null;
  status: 'draft' | 'published' | 'expired';
  owning_tenant: string | null;
  owning_tenant_name: string | null;
  is_reference: boolean;
  content_frozen: boolean;
  can_publish: boolean;
  effective_date: string | null;
  expiry_date: string | null;
  published_at: string | null;
  publication_recorded: boolean;
  authority_approval_body: string | null;
  authority_approval_reference: string | null;
  authority_approval_date: string | null;
  approval_recorded: boolean;
  created_at: string;
}

const NOT_RECORDED = 'Not recorded';

const STATUS_STYLE: Record<ApprovalRecordRow['status'], string> = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-green-100 text-green-800',
  expired: 'bg-amber-100 text-amber-800',
};

function formatDate(value: string | null): string {
  if (!value) return NOT_RECORDED;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * A value the registry never captured. Rendered as the words, in a muted
 * italic, never as an empty cell: an empty cell in an approval table reads as
 * a recorded blank (GCH-35).
 */
const NotRecorded: React.FC = () => (
  <span className="italic text-gray-400">{NOT_RECORDED}</span>
);

function textOrNotRecorded(value: string | null): React.ReactNode {
  return value && value.trim().length > 0 ? value : <NotRecorded />;
}

export const FrameworkVersionsAdmin: React.FC = () => {
  const [rows, setRows] = useState<ApprovalRecordRow[]>([]);
  const [airlines, setAirlines] = useState<AirlineOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [publishing, setPublishing] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishNotice, setPublishNotice] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);

  const { generateDocument, isExporting } = useDocumentAPI({
    onExportSuccess: (blob, filename) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [recordResult, airlineResult] = await Promise.all([
        supabase.rpc('framework_version_approval_records', { p_tenant: null }),
        supabase.from('airlines').select('id, name').order('name'),
      ]);

      if (recordResult.error) throw recordResult.error;
      setRows((recordResult.data ?? []) as ApprovalRecordRow[]);
      setAirlines((airlineResult.data ?? []) as AirlineOption[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load framework versions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleRows = useMemo(() => {
    if (tenantFilter === 'all') return rows;
    if (tenantFilter === 'reference') return rows.filter((r) => r.is_reference);
    return rows.filter((r) => r.owning_tenant === tenantFilter);
  }, [rows, tenantFilter]);

  /**
   * Publication (GCH-26). The database function owns every rule; this only
   * invokes it and reports. The refusal is shown as the database wrote it.
   */
  const publish = useCallback(
    async (row: ApprovalRecordRow) => {
      setPublishing(row.version_id);
      setPublishError(null);
      setPublishNotice(null);
      try {
        const { error: rpcError } = await supabase.rpc('publish_framework_version', {
          p_version_id: row.version_id,
        });
        if (rpcError) {
          setPublishError(rpcError.message);
          return;
        }
        setPublishNotice(
          `Published ${row.version_identifier ?? row.version_id}. The identifier ledger now carries its publication record.`,
        );
        await load();
      } catch (err) {
        setPublishError(err instanceof Error ? err.message : 'Publication failed');
      } finally {
        setPublishing(null);
      }
    },
    [load],
  );

  /**
   * The GCH-28 approval record export (GAT-13 evidence). Every field is taken
   * from the row as the database returned it; nothing is filled in here, so an
   * absent value reaches the generator as null and prints as "Not recorded".
   */
  const exportApprovalRecord = useCallback(
    async (row: ApprovalRecordRow) => {
      setExportingId(row.version_id);
      setError(null);
      try {
        await generateDocument(
          'framework-approval-record',
          'pdf',
          {
            operator: { id: row.owning_tenant, name: row.owning_tenant_name },
            frameworkVersion: {
              frameworkId: row.framework_id,
              versionIdentifier: row.version_identifier,
              versionLabel: row.version_label,
              status: row.status,
              effectiveDate: row.effective_date,
              expiryDate: row.expiry_date,
              isReference: row.is_reference,
            },
            approval: {
              authority: row.authority_approval_body,
              reference: row.authority_approval_reference,
              date: row.authority_approval_date,
            },
            publication: { publishedAt: row.published_at },
            metadata: { generatedAt: new Date().toISOString(), version: '1.0' },
          },
          `WingMentor_Framework_Approval_Record_${row.version_identifier ?? row.version_id}.pdf`,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Approval record export failed');
      } finally {
        setExportingId(null);
      }
    },
    [generateDocument],
  );

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <BadgeCheck className="w-6 h-6" />
            Framework versions and approval records
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            Every competency framework version, its publication record and the operator authority
            approval attached to it. Publication creates a new immutable version identifier that is
            never reused. Assessments already in progress complete against the version they started
            on, so publishing a successor changes nothing already under way.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-navy hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="ml-auto flex items-center gap-2 text-sm text-gray-600">
          Operator
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="all">All</option>
            <option value="reference">WingMentor reference model</option>
            {airlines.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {publishError && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <span className="font-medium">Publication refused: </span>
          {publishError}
        </div>
      )}

      {publishNotice && (
        <div className="mb-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          {publishNotice}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-12 text-gray-500">
          <Loader className="w-5 h-5 animate-spin" />
          Loading framework versions...
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="rounded border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-600">
          No framework versions in this scope.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Operator</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Version</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Status</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Effective</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Published</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">
                  Approval authority
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">
                  Approval reference
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">
                  Approval date
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.version_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-600">
                    {row.is_reference ? (
                      <span className="flex items-center gap-1">
                        <Lock className="w-3.5 h-3.5 text-gray-400" />
                        WingMentor reference model
                      </span>
                    ) : (
                      textOrNotRecorded(row.owning_tenant_name)
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-navy">
                      {textOrNotRecorded(row.version_identifier)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {textOrNotRecorded(row.version_label)}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[row.status]}`}
                    >
                      {row.status}
                    </span>
                    {row.is_reference && (
                      <div className="mt-1 text-xs text-gray-500">
                        Read only in every tenant (CFX-03)
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{formatDate(row.effective_date)}</td>
                  <td className="px-3 py-2 text-gray-600">
                    {row.publication_recorded ? formatDate(row.published_at) : <NotRecorded />}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {textOrNotRecorded(row.authority_approval_body)}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {textOrNotRecorded(row.authority_approval_reference)}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {row.authority_approval_date ? (
                      formatDate(row.authority_approval_date)
                    ) : (
                      <NotRecorded />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-2">
                      {row.can_publish && (
                        <button
                          onClick={() => void publish(row)}
                          disabled={publishing === row.version_id}
                          className="flex items-center gap-1 rounded-md bg-navy px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                        >
                          {publishing === row.version_id ? (
                            <Loader className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <BadgeCheck className="w-3.5 h-3.5" />
                          )}
                          Publish
                        </button>
                      )}
                      <button
                        onClick={() => void exportApprovalRecord(row)}
                        disabled={isExporting && exportingId === row.version_id}
                        className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-navy hover:bg-gray-50 disabled:opacity-50"
                      >
                        {isExporting && exportingId === row.version_id ? (
                          <Loader className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                        Approval record
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 flex items-start gap-2 text-xs text-gray-500">
        <FileText className="mt-0.5 w-3.5 h-3.5 shrink-0" />
        <span>
          "Not recorded" means the registry holds no value: it is not an empty value and not a
          default (GCH-35). The approval record export prints the same distinction, so an absent
          approval is documented as absent rather than exported as a blank form. The registry
          records when a version was published, in an append-only identifier ledger, and holds no
          publisher identity; the publishing actor is recorded in the audit trail instead.
        </span>
      </p>
    </div>
  );
};

export default FrameworkVersionsAdmin;
